import { useState, useEffect } from "react";
import { copyText } from "@/lib/clipboard";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth, useTheme, API } from "@/App";
import axios from "axios";
import { 
  Link2, 
  QrCode, 
  BarChart3, 
  Layout, 
  Moon, 
  Sun, 
  ArrowRight, 
  Zap, 
  Globe, 
  Shield,
  Users,
  TrendingUp,
  Clock,
  Copy,
  Check,
  Menu,
  X
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [shortenedUrl, setShortenedUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState({ total_links: 0, total_clicks: 0, total_users: 0 });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats`);
        setStats(response.data);
      } catch (error) {
        console.error("Failed to fetch stats");
      }
    };
    fetchStats();
  }, []);

  const handleShorten = async (e) => {
    e.preventDefault();
    if (!url) return;

    if (!user) {
      toast.info("Silakan login untuk membuat short link");
      navigate("/login");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API}/links`,
        { original_url: url },
        { withCredentials: true }
      );
      const shortUrl = `${BACKEND_URL}/api/r/${response.data.short_code}`;
      setShortenedUrl(shortUrl);
      toast.success("Link berhasil diperpendek!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal memperpendek link");
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      const ok = await copyText(shortenedUrl);
      if (!ok) throw new Error("copy failed");
      setCopied(true);
      toast.success("Link disalin!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Gagal menyalin link");
    }
  };

  const features = [
    {
      icon: Link2,
      title: "Smart Link Shortener",
      description: "Buat link pendek yang mudah diingat dengan custom alias dan tracking analytics.",
      color: "from-violet-500 to-purple-600"
    },
    {
      icon: Layout,
      title: "Microsite Builder",
      description: "Bangun halaman bio link yang indah dalam hitungan menit. Tanpa coding.",
      color: "from-purple-500 to-pink-600"
    },
    {
      icon: QrCode,
      title: "QR Code Generator",
      description: "QR code dinamis dengan warna kustom. Track scan dan update destinasi kapan saja.",
      color: "from-pink-500 to-rose-600"
    },
    {
      icon: BarChart3,
      title: "Advanced Analytics",
      description: "Insight real-time tentang klik, konversi, dan perilaku audiens.",
      color: "from-rose-500 to-orange-600"
    }
  ];

  const statsDisplay = [
    { value: stats.total_links.toLocaleString() + "+", label: "Links Created" },
    { value: "99.9%", label: "Uptime SLA" },
    { value: stats.total_users.toLocaleString() + "+", label: "Users" },
    { value: "<100ms", label: "Redirect Speed" }
  ];

  return (
   <>
    <Helmet>
      <title>Campus Link - Shortlink Kampus STIKES-HI</title>
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta 
        name="description" 
        content="Platform shortlink kampus STIKES-HI untuk memperpendek link, membuat QR Code, microsite, dan analitik klik."
      />

      <meta 
        name="keywords" 
        content="shortlink kampus, url shortener kampus,short url stikes hi, link pendek STIKES-HI, QR code generator, microsite kampus"
      />

      <meta property="og:title" content="Campus Link - Shortlink Kampus STIKES-HI" />
      <meta property="og:description" content="Perpendek link dan kelola campaign kampus dengan analytics." />
      <meta property="og:image" content="https://s.stikes-hi.ac.id/og-cover.png" />
      <meta property="og:url" content="https://s.stikes-hi.ac.id/" />
      <meta property="og:type" content="website" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Campus Link - Shortlink Kampus STIKES-HI" />
      <meta name="twitter:description" content="Platform shortlink modern untuk civitas akademika." />
      <meta name="twitter:image" content="https://s.stikes-hi.ac.id/og-cover.png" />

      <link rel="canonical" href="https://s.stikes-hi.ac.id/" />
    </Helmet>

    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Link2 className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Campus Link
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <a href="#features" className="text-muted-foreground hover:text-foreground transition-colors">
                Fitur
              </a>
              <a href="#stats" className="text-muted-foreground hover:text-foreground transition-colors">
                Statistik
              </a>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle"
              >
                {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
              {user ? (
                <Button onClick={() => navigate("/dashboard")} data-testid="dashboard-btn">
                  Dashboard
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Button variant="ghost" onClick={() => navigate("/login")} data-testid="login-btn">
                    Login
                  </Button>
                  <Button onClick={() => navigate("/register")} data-testid="register-btn">
                    Daftar Gratis
                  </Button>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={toggleTheme}>
                {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-md">
            <div className="px-4 py-4 space-y-3">
              <a href="#features" className="block text-muted-foreground hover:text-foreground py-2">
                Fitur
              </a>
              <a href="#stats" className="block text-muted-foreground hover:text-foreground py-2">
                Statistik
              </a>
              {user ? (
                <Button className="w-full" onClick={() => navigate("/dashboard")}>
                  Dashboard
                </Button>
              ) : (
                <div className="space-y-2">
                  <Button variant="outline" className="w-full" onClick={() => navigate("/login")}>
                    Login
                  </Button>
                  <Button className="w-full" onClick={() => navigate("/register")}>
                    Daftar Gratis
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="hero-gradient pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <p className="text-sm font-medium tracking-wide uppercase text-primary mb-4 animate-fade-in">
              URL Shortener untuk Kampus STIKES-HI
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight leading-[1.1] mb-6 animate-slide-up" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Platform Link Management{" "}
              <span className="bg-gradient-to-r from-primary to-purple-400 bg-clip-text text-transparent">
                Modern
              </span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-10 animate-fade-in stagger-2">
              All-in-one platform untuk memperpendek link, membuat microsite, generate QR code, 
              dan track semuanya. Dipercaya oleh civitas akademika.
            </p>

            {/* URL Shortener Input */}
            <form onSubmit={handleShorten} className="max-w-2xl mx-auto animate-fade-in stagger-3">
              <div className="link-input-wrapper flex flex-col sm:flex-row gap-3 p-2 rounded-2xl bg-card border border-border shadow-lg">
                <Input
                  type="url"
                  placeholder="Masukkan URL panjang Anda..."
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="flex-1 h-14 text-lg border-0 bg-transparent focus-visible:ring-0"
                  data-testid="url-input"
                />
                <Button 
                  type="submit" 
                  size="lg" 
                  className="h-14 px-8 rounded-xl font-semibold"
                  disabled={isLoading}
                  data-testid="shorten-btn"
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      Perpendek <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </form>

            {/* Shortened URL Result */}
            {shortenedUrl && (
              <div className="mt-6 p-4 rounded-xl bg-card border border-border animate-fade-in">
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <span className="text-muted-foreground">Link pendek Anda:</span>
                  <code className="px-3 py-1.5 rounded-lg bg-primary/10 text-primary font-mono">
                    {shortenedUrl}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={copyToClipboard}
                    data-testid="copy-btn"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            )}

            <p className="text-sm text-muted-foreground mt-4">
              Gratis untuk digunakan. Tidak perlu kartu kredit.
            </p>
          </div>

          {/* Stats Row */}
          <div id="stats" className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20 max-w-4xl mx-auto">
            {statsDisplay.map((stat, index) => (
              <div 
                key={index} 
                className="text-center animate-fade-in"
                style={{ animationDelay: `${0.1 * (index + 1)}s` }}
              >
                <p className="text-3xl md:text-4xl font-bold text-foreground" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                  {stat.value}
                </p>
                <p className="text-sm text-muted-foreground mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-medium tracking-wide uppercase text-primary mb-4">
              Fitur Lengkap
            </p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Semua yang Anda butuhkan untuk berkembang
            </h2>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`feature-card group p-8 rounded-2xl bg-card border border-border hover:border-primary/50 ${
                  index === 0 ? "lg:col-span-2 lg:row-span-1" : ""
                }`}
                data-testid={`feature-card-${index}`}
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                  {feature.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-medium tracking-wide uppercase text-primary mb-4">
              Use Cases
            </p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Dibuat untuk setiap tim
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Users, title: "Dosen & Staff", desc: "Bagikan materi kuliah dengan link singkat", stat: "+40% engagement" },
              { icon: TrendingUp, title: "Marketing", desc: "Track campaign dengan analytics detail", stat: "10x faster setup" },
              { icon: Globe, title: "Organisasi", desc: "Kelola multiple project dengan workspace", stat: "5hrs saved/week" },
              { icon: Zap, title: "Mahasiswa", desc: "Share portfolio dengan bio link custom", stat: "+27% CTR boost" }
            ].map((item, index) => (
              <div key={index} className="p-6 rounded-2xl bg-card border border-border">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <item.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold mb-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                  {item.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-3">{item.desc}</p>
                <p className="text-sm font-medium text-primary">{item.stat}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Siap untuk berkembang lebih cepat?
          </h2>
          <p className="text-lg text-muted-foreground mb-10">
            Bergabung dengan ribuan pengguna yang menggunakan Campus Link untuk hasil yang terukur.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="h-14 px-8" onClick={() => navigate("/register")} data-testid="cta-register-btn">
              Mulai Gratis <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button size="lg" variant="outline" className="h-14 px-8" onClick={() => navigate("/login")} data-testid="cta-login-btn">
              Login
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Link2 className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-bold" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Campus Link
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} STIKES-HI. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Privacy
              </a>
              <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Terms
              </a>
              <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
     </>
  );
};

export default LandingPage;
